import Icon from '@forwardflow/ui/Icons'
import { message, Select } from 'antd'
import { Link, useLocation } from 'react-router-dom'
import { Account } from '../Account'
import useAuthUser from '@forwardflow/hooks/customHooks/useAuthUser'
import { useGetOrganizationList } from '@forwardflow/hooks/customHooks'
import { useEffect, useState, useMemo } from 'react'
import OrganizationIcon from '@forwardflow/assets/svg/OrganizationIcon'
import { useSelectUserOrganizationModalStore } from '@forwardflow/store/modal/SelectUserOrganizationModal'
import { isOperatorEmail } from '@forwardflow/utils/app.utils'
import { useAppStore } from '@forwardflow/store'
import { useUserNavigation } from '@forwardflow/hooks/customHooks/useUserNavigation'
import { SearchField } from '@forwardflow/ui/SearchField'

export const Header: React.FC = () => {
   const LIMIT = 100
   const location = useLocation()
   const { setCurrentOrganization, currentOrganization } = useAppStore()
   const currentPageUrl = location.pathname
   const [hasMore, setHasMore] = useState(true)
   const [isLoadingMore, setIsLoadingMore] = useState(false)
   const [searchValue, setSearchValue] = useState('')
   
   const [organizationParams, setOrganizationParams] = useState({
      page: 1,
      limit: LIMIT,
   })
   const { navigateUser } = useUserNavigation()
   const { openSelectUserOrganizationModal } =
      useSelectUserOrganizationModalStore()
   const { data: user } = useAuthUser({ revalidateOnMount: true })

   const onSelectOrganization = (value: string) => {
      try {
         setCurrentOrganization(value)
         message.success('Organization changed successfully')
         setTimeout(() => {
            navigateUser({
               currentOrgId: value,
               searchParams: location.search,
            })
         }, 1000)
      } catch (error) {
         console.log('error', error)
      }
   }

   const shouldFetch = isOperatorEmail(user?.email || '')

   const { data: organizationList, isLoading } = useGetOrganizationList({
      ...organizationParams,
      shouldFetch,
   })

   const loadMoreOrganizations = () => {
      if (!isLoadingMore && !isLoading && hasMore) {
         setIsLoadingMore(true)
         setOrganizationParams((prev) => ({
            ...prev,
            page: prev.page + 1,
         }))
      }
   }

   const [allOrganizations, setAllOrganizations] = useState<any[]>([])

   useEffect(() => {
      if (organizationList && organizationList.length > 0) {
         setAllOrganizations((prev) => {
            const combined = [...prev, ...organizationList]
            return Array.from(
               new Map(combined.map((item) => [item.id, item])).values()
            )
         })
         if (organizationList.length < LIMIT) {
            setHasMore(false)
         }
         setIsLoadingMore(false)
      }
   }, [organizationList])

   const organizationOptions = allOrganizations?.map((item) => {
      return {
         value: item.id,
         label: item.organizationName + ' - ' + item.id,
      }
   })

   const filteredOptions = useMemo(() => {
      if (!searchValue) return organizationOptions
      return organizationOptions.filter((option) =>
         option.label.toLowerCase().includes(searchValue.toLowerCase())
      )
   }, [organizationOptions, searchValue])

   const currentOrganizationObj = user?.userOrganizations?.find(
      (org) => String(org.organizationId) === currentOrganization
   )

   return (
      <header className="flex flex-wrap px-6 lg:px-11">
         <div className="flex items-center self-center order-1">
            <a className="shrink-0" href="#">
               <img
                  src="/images/ff-sm-logo.png"
                  className="w-8 lg:w-auto"
                  alt="forward-flow-logo"
               />
            </a>
         </div>
         {currentPageUrl === '/dashboard' && (
            <div className="flex items-center self-center order-3 w-full lg:w-auto lg:order-2">
               <Icon
                  name="Hamburger"
                  className="ml-3 cursor-pointer lg:hidden"
               />
               <div className="ml-6 lg:ml-16 lg:text-[24px] font-medium text-white/70">
                  Dashboard
               </div>
            </div>
         )}

         <div className="flex items-center justify-end flex-1 order-2 py-3 gap-x-4 lg:order-3 ">
            {user?.userOrganizations?.length === 1 ||
            user?.email === 'operator@forward-flow.com' ? null : (
               <div
                  onClick={openSelectUserOrganizationModal}
                  className=" h-[50px] flex items-center bg-white bg-opacity-[0.04] hover:bg-opacity-[0.08] p-2 rounded text-sm w-56 cursor-pointer"
               >
                  <span className="bg-[#27282C] rounded mr-6 flexc w-9 h-9">
                     <span className="scale-95">
                        <OrganizationIcon />
                     </span>
                  </span>
                  <span>
                     {currentOrganizationObj?.organization?.name ||
                        'Organization'}
                  </span>
               </div>
            )}
            {!currentPageUrl.includes('/demo-conversations') &&
               (user?.email === 'operator@forward-flow.com' ||
                  user?.email === 'brannon@forward-flow.com' ||
                  user?.email === 'engineered@forward-flow.com') && (
                  <Link
                     to="/demo-conversations"
                     className="bg-[#D9D9D9]/40 opacity-80 flexc h-12 border-white/30 border !m-0 !rounded !w-[120px] gap-x-3 hover:bg-[#D9D9D9]/60 hover:border-white"
                  >
                     <Icon name="Message" className=" fill-white" />
                     <span>Demo</span>
                  </Link>
               )}
            {(user?.email === 'operator@forward-flow.com' ||
               user?.email === 'brannon@forward-flow.com' ||
               user?.email === 'engineered@forward-flow.com') && (
               <>
                  <Select
                     className="ml-[10px] w-[120px] lg:w-[200px] dashboard-select"
                     placeholder="Select Organization"
                     options={filteredOptions}
                     onChange={onSelectOrganization}
                     value={currentOrganization || ''}
                     loading={isLoading || isLoadingMore}
                     notFoundContent={<div className="hidden" />}
                     onPopupScroll={(e) => {
                        const target = e.target as HTMLDivElement
                        const threshold = 50
                        const isNearBottom =
                           target.scrollTop + target.offsetHeight >=
                           target.scrollHeight - threshold

                        if (isNearBottom && hasMore && !searchValue) {
                           loadMoreOrganizations()
                        }
                     }}
                     popupRender={(orgList) => (
                        <div>
                           <div>
                              <SearchField
                                 placeholder="Search organizations"
                                 className="!text-[14px] !bg-transparent !text-white gap-1"
                                 value={searchValue}
                                 onChange={(value) => setSearchValue(value)}
                                 iconClassName='!fill-white/70 scale-75 !px-0 !ml-2'
                              />
                           </div>
                           {orgList}
                           {!searchValue && isLoadingMore && (
                              <div className="text-center text-white/70 text-xs py-2">
                                 Loading more...
                              </div>
                           )}
                           {searchValue && filteredOptions.length === 0 && (
                              <div className="text-center py-2 text-white/70 text-xs">
                                 No organizations found
                              </div>
                           )}
                        </div>
                     )}
                  />
               </>
            )}

            {/* {currentPageUrl === '/dashboard' && (
               <>
                  <div
                     onClick={openSearchModal}
                     className="flex items-center justify-center rounded-full cursor-pointer lg:hidden w-9 h-9 bg-white/10"
                  >
                     <Icon
                        name="search"
                        className="text-[#fff] scale-90 flexc"
                     />
                  </div>
                  <SearchField
                     onclick={openSearchModal}
                     className="hidden lg:flex"
                  />
               </>
            )} */}
            {/* <Notification /> */}
            <Account />
         </div>
      </header>
   )
}
